'use client';

import { type FC, useCallback, useEffect, useState } from 'react';
import { useCheckAdAvailableQuery, useCompleteAdMutation } from '../../api';
import type { AdType } from '../../type';
import { AdOverlay } from './AdOverlay';
import { VideoWatchNotCompletedModal, VideoWatchNoVideosModal, VideoWatchSuccessModal } from './modals';
type FlowState = 'idle' | 'checking' | 'showing_ad' | 'completing' | 'success' | 'error' | 'no_ads';
interface WatchGoogleAdsFlowProps {
  isActive: boolean;
  onClose: () => void;
}
export const WatchGoogleAdsFlow: FC<WatchGoogleAdsFlowProps> = ({
  isActive,
  onClose
}) => {
  const [flowState, setFlowState] = useState<FlowState>('idle');
  const [adData, setAdData] = useState<{
    adType: AdType;
    googleAdsenseUnitId: string;
  } | null>(null);
  const [rewardAmount, setRewardAmount] = useState(0);
  const {
    refetch
  } = useCheckAdAvailableQuery(false);
  const completeAdMutation = useCompleteAdMutation();
  const checkAvailability = useCallback(async () => {
    setFlowState('checking');
    try {
      const {
        data
      } = await refetch();
      if (!data || !data.available) {
        setFlowState('no_ads');
        return;
      }
      setAdData({
        adType: data.adType,
        googleAdsenseUnitId: data.googleAdsenseUnitId
      });
      setFlowState('showing_ad');
    } catch {
      setFlowState('error');
    }
  }, [refetch]);
  useEffect(() => {
    if (isActive && flowState === 'idle') {
      checkAvailability();
    }
  }, [isActive, flowState, checkAvailability]);
  const handleClose = useCallback(() => {
    setFlowState('idle');
    setAdData(null);
    setRewardAmount(0);
    onClose();
  }, [onClose]);
  const handleAdCompleted = useCallback(async (watchedDuration: number) => {
    if (!adData) return;
    setFlowState('completing');
    try {
      const result = await completeAdMutation.mutateAsync({
        adType: adData.adType,
        watchedDuration,
        wasCompleted: true
      });
      setRewardAmount(result.poeReward);
      setFlowState('success');
    } catch {
      setFlowState('error');
    }
  }, [adData, completeAdMutation]);
  const handleAdDismissed = useCallback(async (watchedDuration_0: number) => {
    if (!adData) return;
    try {
      await completeAdMutation.mutateAsync({
        adType: adData.adType,
        watchedDuration: watchedDuration_0,
        wasCompleted: false
      });
    } catch {
      // silent — user chose to leave, no reward expected
    }
    handleClose();
  }, [adData, completeAdMutation, handleClose]);
  const handleTryAgain = useCallback(() => {
    setFlowState('idle');
    setAdData(null);
    checkAvailability();
  }, [checkAvailability]);
  const handleWatchMore = useCallback(() => {
    setFlowState('idle');
    setAdData(null);
    setRewardAmount(0);
    checkAvailability();
  }, [checkAvailability]);
  if (!isActive && flowState === 'idle') return null;
  return <>
      {/* Ad Overlay */}
      {flowState === 'showing_ad' && adData && <AdOverlay adType={adData.adType} googleAdsenseUnitId={adData.googleAdsenseUnitId} onCompleted={handleAdCompleted} onDismissed={handleAdDismissed} />}

      {/* Loading state */}
      {(flowState === 'checking' || flowState === 'completing') && <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-white/80" />
        </div>}

      {/* No ads available */}
      <VideoWatchNoVideosModal isOpen={flowState === 'no_ads'} onClose={handleClose} onOk={handleClose} />

      {/* Error / not completed */}
      <VideoWatchNotCompletedModal isOpen={flowState === 'error'} onClose={handleClose} onCancel={handleClose} onTryAgain={handleTryAgain} />

      {/* Success */}
      <VideoWatchSuccessModal isOpen={flowState === 'success'} onClose={handleClose} onVoteMore={handleWatchMore} onBack={handleClose} rewardAmount={rewardAmount} />
    </>;
};